import { useEffect, useState } from 'react';
import { Button } from 'shared/src/components/ui/new';
import { useLmsMedia } from './lmsMediaContext';

type LearnHousePdfViewerProps = {
  courseUuid: string;
  activityUuid: string;
  title: string;
};

export default function LearnHousePdfViewer({ courseUuid, activityUuid, title }: LearnHousePdfViewerProps) {
  const { fetchers } = useLmsMedia();
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;

    async function load() {
      if (attempt < 0) {
        return;
      }
      setIsLoading(true);
      setError(null);
      setObjectUrl(null);

      try {
        const blob = await fetchers.getActivityPdf(courseUuid, activityUuid);
        if (cancelled) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setObjectUrl(createdUrl);
      } catch (loadError) {
        console.error(loadError);
        if (!cancelled) {
          setError('Failed to load this PDF.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [attempt, courseUuid, activityUuid, fetchers]);

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading PDF…</div>;
  }

  if (error || !objectUrl) {
    return (
      <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
        <p>This document didn't load.</p>
        <Button type="button" variant="outline" size="sm" className="mt-2" onClick={() => setAttempt((value) => value + 1)}>
          Try again
        </Button>
      </div>
    );
  }

  return <iframe title={title} src={objectUrl} className="h-[80vh] w-full rounded border bg-white" />;
}
